import React from 'react';
import bearing from '@turf/bearing';
import { getUnixTime } from 'date-fns';

import {
    useDebriefDispatch,
    useDebriefRouter,
    useDebriefSelector,
} from 'ff/debrief/hooks';
import {
    extractTrimmedValues,
    findCurrentSegment,
    findNearestPoint,
    trackPointToGeojsonPoint,
} from 'ff/debrief/utils';
import { interpolatePointAtTimestamp } from 'ff/debrief/components/charts/utils/interpolation';
import * as chronoStore from 'ff/debrief/features/ChronoSlice';
import * as appStore from 'ff/debrief/features/AppSlice';
import * as api from 'ff/debrief/features/TracklogApi';

export function useChrono() {
    const state = useDebriefSelector(s => s.chronoSlice);
    const dispatch = useDebriefDispatch();
    const { actions: chronoActions } = chronoStore;
    const { actions: appActions } = appStore;

    const {
        routeParams: { tracklogId },
    } = useDebriefRouter();
    const { data: tl } = api.useGetTracklogByIdQuery(tracklogId, {
        skip: !tracklogId,
    });
    const { data: debrief } = api.useGetDebriefByTrackUUIDQuery(tracklogId, {
        skip: !tracklogId,
    });

    const trimmedValues = extractTrimmedValues(tl);
    const currentTime = trimmedValues?.timestampStart + state.currentOffset;
    const points = tl?.points?.data;

    const currentPoint = React.useMemo(() => {
        if (!points || !tl?.points?.columns) return null;

        // Find the nearest point index
        const nearestIdx = findNearestPoint(points, currentTime);
        const nearestTrackPoint = points[nearestIdx];
        const nearestTimestamp = nearestTrackPoint[0];

        // Determine which two points to interpolate between
        let point1: number[], point2: number[];

        if (nearestTimestamp <= currentTime && nearestIdx < points.length - 1) {
            // Current time is at or after the nearest point, interpolate forward
            point1 = nearestTrackPoint;
            point2 = points[nearestIdx + 1];
        } else if (nearestTimestamp > currentTime && nearestIdx > 0) {
            // Current time is before the nearest point, interpolate backward
            point1 = points[nearestIdx - 1];
            point2 = nearestTrackPoint;
        } else {
            // At the boundary (start or end), use the nearest point directly
            point1 = nearestTrackPoint;
            point2 = nearestTrackPoint;
        }

        // Use interpolation if we have two different points
        let interpolatedPoint: number[];
        if (point1 !== point2) {
            interpolatedPoint =
                interpolatePointAtTimestamp(
                    point1,
                    point2,
                    currentTime,
                    tl.points.columns
                ) || nearestTrackPoint; // Fallback to nearest point if interpolation fails
        } else {
            interpolatedPoint = nearestTrackPoint;
        }

        // Calculate bearing using the nearest point for geospatial calculations
        const nextTrackPoint = points[nearestIdx + 1];
        const prevTrackPoint = points[nearestIdx - 1];
        const nearestPoint = trackPointToGeojsonPoint(nearestTrackPoint);
        const nextPoint = trackPointToGeojsonPoint(nextTrackPoint);
        const prevPoint = trackPointToGeojsonPoint(prevTrackPoint);
        const currentBearing = nextTrackPoint
            ? bearing(nearestPoint, nextPoint)
            : bearing(prevPoint, nearestPoint);

        return {
            timestamp: interpolatedPoint[0],
            latitude: interpolatedPoint[1],
            longitude: interpolatedPoint[2],
            groundSpeed: interpolatedPoint[3],
            altitudeFeet: interpolatedPoint[4],
            cabinAltitudeFeet: interpolatedPoint[5],
            bankDeg: interpolatedPoint[6],
            pitchDeg: interpolatedPoint[7],
            gLoad: interpolatedPoint[8],
            trueAirSpeed: interpolatedPoint[9],
            magneticCourse: interpolatedPoint[10],
            magneticHeading: interpolatedPoint[11],
            windDirection: interpolatedPoint[12],
            windVelocity: interpolatedPoint[13],
            verticalSpeed: interpolatedPoint[14],
            turnRate: interpolatedPoint[15],
            magneticVariance: interpolatedPoint[16],
            indicatedAirSpeed: interpolatedPoint[17],
            crosswind: interpolatedPoint[18],
            headwind: interpolatedPoint[19],
            aboveGroundLevel: interpolatedPoint[20],
            bearing: currentBearing,
        };
    }, [currentTime, points, tl?.points?.columns]);

    const segments = debrief?.debriefSegments;

    const play = () => {
        dispatch(chronoStore.playTicker(tracklogId));
    };

    const pause = () => {
        dispatch(chronoActions.pauseTicker());
    };

    const setOffset = (offset: number) => {
        const currentSegment = findCurrentSegment(segments, offset);
        dispatch(chronoActions.setOffset(offset));
        dispatch(appActions.setSelectedSegment(currentSegment));
    };

    const setTime = (time: number | Date) => {
        const wasTicking = state.ticker;
        if (state.ticker) {
            dispatch(chronoActions.pauseTicker());
        }
        const newTime = typeof time === 'number' ? time : getUnixTime(time);
        const newOffset = Math.abs(trimmedValues?.timestampStart - newTime);
        const currentSegment = findCurrentSegment(segments, newOffset);
        dispatch(chronoActions.setOffset(newOffset));
        dispatch(appActions.setSelectedSegment(currentSegment));
        if (wasTicking) dispatch(chronoStore.playTicker(tracklogId));
    };

    const setSpeed = (speed: number) => {
        dispatch(chronoStore.setSpeed(speed));
    };

    const resetChrono = () => {
        dispatch(chronoActions.reset());
    };

    return {
        offset: state.currentOffset,
        speed: state.tickerSpeed,
        currentTime: currentTime,
        currentPoint: currentPoint,
        isTicking: Boolean(state.ticker),
        play: play,
        pause: pause,
        setOffset: setOffset,
        setTime: setTime,
        setSpeed: setSpeed,
        resetChrono: resetChrono,
    };
}
